import { useCallback, useEffect, useRef } from "react";

interface UseSafeClickHandlerOptions {
  onClick: () => void;
  delay?: number;
}

export const useSafeClickHandler = ({ onClick, delay = 300 }: UseSafeClickHandlerOptions) => {
  const clickTimeoutRef = useRef<NodeJS.Timeout>();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't prevent default or stop propagation
      // Let child components handle their own event management
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }

      // Delay the click to allow for double-click detection
      clickTimeoutRef.current = setTimeout(() => {
        onClick();
      }, delay);
    },
    [onClick, delay],
  );

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    // Cancel the pending click - double-click detected
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  return {
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
  };
};
